import { Button, Col, Form, Input, Popover, Row, Table } from 'antd';
import { ColumnProps, TableRowSelection } from 'antd/lib/table';
import { onConfirmAdminMailSendSettingDeletion, onConfirmAdminMailSendSettingRegistration } from 'domain/middleware';
import { IMailSendSettingMail } from 'domain/model';
import { getState } from 'domain/store';
import moment from 'moment';
import 'moment/locale/ja';
import React, { useState } from 'react';
import './mailSendSetting.css';

/**
 * メール送信設定コンポーネント
 * @param props プロパティ
 * @returns コンポーネント
 */
const MailSendSetting = (props: any): JSX.Element => {
  /**
   * 明細データソースIF
   */
  interface IDataSource {
    companyId: string;
    companyName: JSX.Element;
    mailBody: JSX.Element;
    insertUserName: JSX.Element;
    insertDatetime: string;
    updateDatetime: string;
  }

  // State取得
  const state = getState();
  const { companyId, mailBody, mailList } = getState().mailSendSetting;

  // 選択行の企業ID
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[] | number[]>(
    mailList.filter(mail => !mail.validFlag).map(mail => mail.companyId)
  );

  /** 列定義 */
  const COLUMNS: ColumnProps<IDataSource>[] = [
    { title: '企業ID', dataIndex: 'companyId', key: 'companyId', className: 'companyId center' },
    { title: '企業名', dataIndex: 'companyName', className: 'companyName' },
    { title: 'メール本文', dataIndex: 'mailBody', className: 'mailBody' },
    { title: '登録者', dataIndex: 'insertUserName', className: 'insertUserName' },
    { title: '登録日時', dataIndex: 'insertDatetime', className: 'insertDatetime center' }
  ];

  /** 各種日時フォーマット */
  const DATETIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';

  /**
   * 登録ボタン押下処理
   * @param e イベント
   */
  const handleRegister = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.preventDefault();

    // バリデーションチェック
    props.form
      .validateFields()
      .then(async (fieldsValue: any) => {
        // バリデーションチェックが正常の場合
        // 登録ボタン押下処理
        await onConfirmAdminMailSendSettingRegistration(fieldsValue, selectedRowKeys);
      })
      .catch((e: any) => {
        // バリデーションチェックがエラーの場合
        // 処理なし
      });
  };

  /**
   * 削除ボタン押下処理
   * @param e イベント
   */
  const handleDelete = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.preventDefault();

    const COMPANY_TABLE_COLUMNS: ColumnProps<IMailSendSettingMail>[] = [
      { title: '企業ID', dataIndex: 'companyId', key: 'companyId', className: 'companyId center' },
      { title: '企業名', dataIndex: 'companyName', className: 'companyName' }
    ];

    // 削除ボタン押下処理
    await onConfirmAdminMailSendSettingDeletion(
      props.form.getFieldsValue(),
      selectedRowKeys,
      (deletionMailList: IMailSendSettingMail[]) => (
        <>
          <Table
            className={'companyTable'}
            columns={COMPANY_TABLE_COLUMNS}
            dataSource={deletionMailList}
            rowKey="companyId"
            pagination={false}
            tableLayout={'auto'}
            scroll={{
              y: 162
            }}
          />
        </>
      )
    );
  };

  // 行選択
  const rowSelection: TableRowSelection<IDataSource> = {
    /**
     * 行選択の変更
     * @param selectedRowKeys 選択行の企業ID
     * @param selectedRows 選択行
     */
    onChange: (selectedRowKeys: string[] | number[], selectedRows: IDataSource[]) => {
      // 選択行の企業IDを保持
      setSelectedRowKeys(selectedRowKeys);
    },
    /**
     * チェックボックスプロパティの取得
     * @param row 行
     * @returns チェックボックスプロパティ
     */
    getCheckboxProps: (row: any) => ({
      name: row.name
    }),
    // 選択行の企業ID
    selectedRowKeys
  };

  // 登録メール一覧用のデータソースの生成
  const dataSource: IDataSource[] = state.mailSendSetting.mailList.map((mail, index) => {
    return {
      companyId: mail.companyId,
      companyName: (
        <div className="companyName">
          <Popover
            placement="topLeft"
            content={
              <div>
                <pre className="popoverPre companyNamePopover">{mail.companyName}</pre>
              </div>
            }
          >
            <span>{mail.companyName}</span>
          </Popover>
        </div>
      ),
      mailBody: (
        <div className="mailBody">
          <Popover
            placement="topLeft"
            content={
              <div>
                <pre className="popoverPre mailBodyPopover">{mail.mailBody}</pre>
              </div>
            }
          >
            <span>{mail.mailBody}</span>
          </Popover>
        </div>
      ),
      insertUserName: (
        <div className="insertUserName">
          <Popover
            placement="topLeft"
            content={
              <div>
                <pre className="popoverPre insertUserNamePopover">{mail.insertUserName}</pre>
              </div>
            }
          >
            <span>{mail.insertUserName}</span>
          </Popover>
        </div>
      ),
      insertDatetime: moment(mail.insertDatetime).format(DATETIME_FORMAT),
      updateDatetime: moment(mail.updateDatetime).format(DATETIME_FORMAT)
    };
  });

  const { getFieldDecorator } = props.form;

  return (
    <Form className="mailSendSetting" layout="horizontal" form={props.form}>
      <Row className="area">
        <Col>
          <Row>
            <Col>
              <Form.Item label="企業ID">
                {getFieldDecorator('companyId', {
                  initialValue: companyId,
                  rules: [
                    {
                      // 必須チェック
                      required: true,
                      message: '企業IDを入力してください'
                    },
                    {
                      // 桁数チェック
                      pattern: /^\d{10}$/,
                      message: '企業IDは半角数字10文字で入力してください'
                    }
                  ]
                })(<Input className="companyId" maxLength={10} />)}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item label="メール本文">
                {getFieldDecorator('mailBody', {
                  initialValue: mailBody,
                  rules: [
                    {
                      // 必須チェック
                      required: true,
                      message: 'メール本文を入力してください'
                    }
                  ]
                })(<Input.TextArea className="mailBody" rows={4} maxLength={500} />)}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button className="registerBtn" onClick={async e => await handleRegister(e)}>
                登録
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="area">
        <Col>
          <Button className="deleteBtn" disabled={!selectedRowKeys.length} onClick={e => handleDelete(e)}>
            削除
          </Button>
          <Table
            className={'commonTable'}
            rowSelection={rowSelection}
            columns={COLUMNS}
            dataSource={dataSource}
            rowKey="companyId"
            pagination={false}
            size={'small'}
          />
        </Col>
      </Row>
    </Form>
  );
};

export const MailSendSettingForm: any = Form.create()(MailSendSetting);
